import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Microphone,
  StopCircle,
  ArrowRight,
  Spinner,
  Compass,
  Lightning,
  ArrowLeft,
} from "@phosphor-icons/react";
import { api } from "../lib/api";
import useRecorder from "../hooks/useRecorder";
import Disclaimer from "../components/Disclaimer";

const RECENT_KEY = "ccfl_recent_walkthrough";

function RouteCard({ icon: Icon, label, blurb, accent, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="group text-left bg-white border border-[#E8E4DB] rounded-2xl p-7 sm:p-8 hover:border-[#1E3A2B] hover:shadow-md transition-all"
    >
      <div className="flex items-center gap-3">
        <span
          className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 ${accent}`}
        >
          <Icon size={22} weight="duotone" />
        </span>
        <div className="label-kicker">{label.shoulder}</div>
      </div>
      <h3 className="font-serif text-2xl sm:text-3xl text-[#1E3A2B] mt-4 leading-tight">
        {label.title}
      </h3>
      <p className="text-[#4A524C] mt-3 leading-relaxed">{blurb}</p>
      <div className="mt-5 inline-flex items-center gap-1.5 text-sm text-[#1E3A2B] font-medium">
        Continue <ArrowRight size={14} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
      </div>
    </button>
  );
}

function ResumeBanner({ recent, onResume, onDismiss }) {
  return (
    <div
      className="bg-[#F3F0EA] border border-[#1E3A2B]/15 rounded-2xl p-5 mb-8 anim-in delay-1"
      data-testid="resume-banner"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="label-kicker">Active engagement</div>
          <p className="text-[#1E3A2B] mt-2 leading-relaxed">
            You have a session in progress — <span className="italic text-[#4A524C]">"{recent.preview}"</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onDismiss}
            data-testid="resume-dismiss"
            className="text-sm text-[#8A948E] hover:text-[#1E3A2B] px-3 py-2"
          >
            Start fresh
          </button>
          <button
            onClick={onResume}
            data-testid="resume-continue"
            className="inline-flex items-center gap-1.5 bg-[#1E3A2B] text-white px-4 py-2 rounded-xl text-sm hover:bg-[#2C4C3B]"
          >
            Continue <ArrowRight size={14} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
}

function ComposerCard({ mode, onBack, onSubmit, loading }) {
  const [description, setDescription] = useState("");
  const [transcribing, setTranscribing] = useState(false);
  const { recording, start, stop } = useRecorder();

  const handleMic = async () => {
    if (!recording) {
      await start();
      return;
    }
    const blob = await stop();
    if (!blob) return;
    setTranscribing(true);
    try {
      const ext = blob.type.includes("mp4") ? "mp4" : "webm";
      const form = new FormData();
      form.append("audio", blob, `recording.${ext}`);
      const { data } = await api.post("/transcribe", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDescription((prev) => (prev ? prev + " " + data.text : data.text));
    } catch {
      toast.error("Could not transcribe. You can type instead.");
    } finally {
      setTranscribing(false);
    }
  };

  const submit = (e) => {
    e?.preventDefault();
    if (description.trim().length < 5) {
      toast.error("Please share a few sentences so the coach has context.");
      return;
    }
    onSubmit(description.trim());
  };

  const isQuick = mode === "quick";
  const heading = isQuick ? "What's Your Question?" : "Guide Me Through a Difficult Conversation.";
  const sub = "";
  const intro = isQuick
    ? ""
    : "Start by briefing the coach below — a few sentences is enough. Don't worry. Your Coach will guide you through your conversation step-by-step.";
  const placeholder = isQuick
    ? "I have to talk to my cofounder about scope creep tomorrow. How do I open the conversation without putting them on the defensive?"
    : "A cofounder undermined my decision in front of the team this morning. I need to handle it before our next stand-up…";

  return (
    <form onSubmit={submit} className="anim-in delay-2">
      <Link
        to="/"
        className="inline-flex items-center gap-1.5 text-sm text-[#4A524C] hover:text-[#1E3A2B] mb-5"
        data-testid="composer-back"
      >
        <ArrowLeft size={14} weight="bold" /> Choose a different form of engagement
      </Link>
      <div className="label-kicker">{isQuick ? "Targeted brief" : "Active engagement"}</div>
      <h1 className="font-serif text-4xl sm:text-5xl text-[#1E3A2B] tracking-tight leading-tight mt-3">
        {heading}
      </h1>
      {intro && <p className="text-[#4A524C] mt-4 leading-relaxed max-w-2xl">{intro}</p>}
      {sub && <p className="text-[#4A524C] mt-3 leading-relaxed max-w-2xl text-sm italic">{sub}</p>}

      <div className="mt-8 relative">
        <textarea
          data-testid="walkthrough-description-input"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={isQuick ? 5 : 7}
          autoFocus
          placeholder={placeholder}
          className="w-full bg-white border border-[#E8E4DB] focus:border-[#1E3A2B] focus:ring-2 focus:ring-[#768C7E]/30 focus:outline-none rounded-2xl p-5 text-[#121815] placeholder-[#8A948E] text-base leading-relaxed resize-none transition-all"
        />
        <button
          type="button"
          onClick={handleMic}
          disabled={transcribing}
          data-testid="walkthrough-mic-button"
          className={`absolute bottom-4 right-4 w-11 h-11 rounded-full flex items-center justify-center transition-all ${
            recording ? "mic-recording" : "bg-[#1E3A2B] text-white hover:bg-[#2C4C3B]"
          } disabled:opacity-50`}
          title={recording ? "Stop recording" : "Speak instead"}
        >
          {transcribing ? (
            <Spinner size={20} weight="bold" className="animate-spin" />
          ) : recording ? (
            <StopCircle size={22} weight="fill" />
          ) : (
            <Microphone size={22} weight="fill" />
          )}
        </button>
      </div>

      <div className="mt-8 flex items-center gap-3">
        <button
          type="submit"
          disabled={loading}
          data-testid="walkthrough-submit-button"
          className="inline-flex items-center gap-2 bg-[#1E3A2B] text-white px-8 py-4 rounded-xl font-medium hover:bg-[#2C4C3B] disabled:opacity-60 transition-colors"
        >
          {loading ? (
            <>
              <Spinner size={18} className="animate-spin" /> Engaging…
            </>
          ) : (
            <>
              {isQuick ? "Next" : "Begin engagement"} <ArrowRight size={18} weight="bold" />
            </>
          )}
        </button>
        <span className="text-xs text-[#8A948E]">Response in seconds. Confidential.</span>
      </div>
      <Disclaimer variant="compact" className="mt-6 max-w-2xl" />
    </form>
  );
}

export default function Walkthrough() {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialMode =
    searchParams.get("mode") === "quick" || searchParams.get("mode") === "guided"
      ? searchParams.get("mode")
      : null;
  const [mode, setMode] = useState(initialMode); // null = picker; 'guided' | 'quick' = composer
  const [loading, setLoading] = useState(false);
  const [recent, setRecent] = useState(null);

  // Keep ?mode= in sync when user clicks a card or goes back to picker
  useEffect(() => {
    const current = searchParams.get("mode");
    if (mode && current !== mode) {
      setSearchParams({ mode }, { replace: true });
    } else if (!mode && current) {
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Keep local `mode` in sync when the user navigates via the top nav while
  // already on /walkthrough (URL changes but component doesn't remount)
  useEffect(() => {
    const urlMode = searchParams.get("mode");
    if (urlMode === "quick" || urlMode === "guided") {
      if (mode !== urlMode) setMode(urlMode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Detect a recent in-progress walkthrough so we can offer "continue"
  useEffect(() => {
    let alive = true;
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return;
    let saved;
    try {
      saved = JSON.parse(raw);
    } catch {
      localStorage.removeItem(RECENT_KEY);
      return;
    }
    if (!saved?.id) return;
    api
      .get(`/walkthrough/${saved.id}`)
      .then(({ data }) => {
        if (!alive) return;
        if (data?.id) {
          const preview = (data.description || "").slice(0, 120);
          setRecent({ id: data.id, preview });
        }
      })
      .catch(() => {
        localStorage.removeItem(RECENT_KEY);
      });
    return () => {
      alive = false;
    };
  }, []);

  const submit = async (description) => {
    setLoading(true);
    try {
      const { data } = await api.post("/walkthrough/start", {
        description,
        need: null,
        mode,
      });
      // Remember as the leader's recent walkthrough so the resume banner appears
      // next time they open /walkthrough.
      localStorage.setItem(
        RECENT_KEY,
        JSON.stringify({ id: data.id, mode, started_at: new Date().toISOString() })
      );
      nav(`/walkthrough/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start.");
    } finally {
      setLoading(false);
    }
  };

  if (mode) {
    return (
      <div className="max-w-3xl mx-auto px-6 sm:px-8 py-12 lg:py-20">
        <ComposerCard
          mode={mode}
          loading={loading}
          onBack={() => setMode(null)}
          onSubmit={submit}
        />
      </div>
    );
  }

  // The picker is deprecated — if no mode is set, send the user to the home
  // hero where the 3 choice cards already live.
  return <Navigate to="/" replace />;
}
